import { FeatureFlagCode, FeatureFlagKv, FeatureFlagStatus } from '__generated__/graphql';
import React, { createContext, ReactNode, useContext } from 'react';
import { useAuthContext } from './auth.context';
import { useGetAllTenantFeatureFlags } from 'hooks/tenants/queries/useGetAllTenantFeatureFlags';
import { ApolloError } from '@apollo/client';

interface FeatureFlagContextInterface {
  featureFlags: FeatureFlagKv[];
  featureFlagsLoading: boolean;
  featureFlagsError: ApolloError | null;
  refetchFeatureFlags: () => void;
  checkFeatureFlagIsEnabled: (featureFlag: FeatureFlagCode) => boolean;
  checkFeatureFlags: (flags: FeatureFlagCode[], mode?: 'any' | 'all') => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextInterface>({
  featureFlags: [],
  featureFlagsLoading: false,
  featureFlagsError: null,
  refetchFeatureFlags: () => {},
  checkFeatureFlagIsEnabled: () => false,
  checkFeatureFlags: () => false,
});

export const FeatureFlagContextProvider = ({ children }: { children: ReactNode }) => {
  const { userTenantId } = useAuthContext();
  const { data, loading, error, refetch } = useGetAllTenantFeatureFlags(userTenantId ?? 0);
  const featureFlags: FeatureFlagKv[] = data?.getFeatureFlags?.featureFlags ?? [];

  const checkFeatureFlagIsEnabled = (featureFlag: FeatureFlagCode) => {
    return (
      featureFlags.find(({ key }) => key === featureFlag)?.value?.status ===
      FeatureFlagStatus.Active
    );
  };

  const checkFeatureFlags = (flags: FeatureFlagCode[], mode: 'any' | 'all' = 'any') => {
    const check = (flag: FeatureFlagCode) =>
      featureFlags.find(({ key }) => key === flag)?.value?.status === FeatureFlagStatus.Active;
    return mode === 'all' ? flags.every(check) : flags.some(check);
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags,
        featureFlagsLoading: loading,
        featureFlagsError: error || null,
        refetchFeatureFlags: refetch,
        checkFeatureFlagIsEnabled,
        checkFeatureFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);
